import React, { ComponentProps } from 'react';
import clamp from 'lodash/clamp';
import styled, { css } from 'styled-components';
import { up } from '../../../utils/styles/breakpoints';
import { IconBookmark } from '../../icon/IconBookmark';
import { IconBookmarkFilled } from '../../icon/IconBookmarkFilled';
import { IconDislike } from '../../icon/IconDislike';
import { IconDislikeFilled } from '../../icon/IconDislikeFilled';
import { IconLike } from '../../icon/IconLike';
import { IconThumbDown } from '../../icon/IconThumbDown';
import { IconThumbDownFilled } from '../../icon/IconThumbDownFilled';
import { IconThumbUp } from '../../icon/IconThumbUp';
import { IconThumbUpFilled } from '../../icon/IconThumbUpFilled';
import { Box } from '../../layout/Box';
import { DataTestId } from '../../layout/DataTestIdWrapper';
import { Flex } from '../../layout/Flex';

const MIN_POST_RATIO = 9 / 16;

export const Root = styled(Box)
    .withConfig({ displayName: 'Root' })
    .attrs((props) => ({
        ...props,
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        as: 'article',
    }))`
    -webkit-user-select: none;
    user-select: none;
    transform: translateZ(0px);
`;

export const Header = styled(Flex)
    .withConfig({ displayName: 'Header' })
    .attrs((props) => ({
        ...props,
        as: 'header',
        direction: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        position: 'absolute',
        top: 0,
        left: 0,
        p: 2,
        width: '100%',
        zIndex: 5,
        spacing: 1,
    }))``;

export const Content = styled(Flex)
    .withConfig({ displayName: 'Content' })
    .attrs((props) => ({
        ...props,
        position: 'absolute',
        direction: 'column',
        justifyContent: 'flex-end',
        px: 1,
        pb: 1.5,
        pt: 7.5,
        bottom: 0,
        left: 0,
        width: '100%',
        zIndex: 2,
    }))``;

interface ShadowProps {
    isVideo?: boolean;
    isMobile: boolean;
    isVisible: boolean;
    isExpanded?: boolean;
    isProductExpanded?: boolean;
}

export const Shadow = styled(Box)
    .withConfig({ displayName: 'Shadow' })
    .attrs((props) => ({
        ...props,
        position: 'absolute',
        width: '100%',
        height: '100%',
        background: 'rgba(0, 0, 0, 0.70)',
        transition: '200ms linear opacity',
        cursor: 'pointer',
    }))<ShadowProps>`
            z-index: ${({ isVisible }) => (isVisible ? 1 : 10)};

            opacity: ${({ isExpanded, isVisible, isMobile }) =>
                isExpanded || (!isVisible && isMobile) ? 1 : 0};

            pointer-events: ${({ isExpanded, isProductExpanded, isVideo }) => {
                if ((isExpanded && isVideo) || (isProductExpanded && (isExpanded || isVideo))) {
                    return 'auto';
                }

                return 'none';
            }}
        `;

interface MediaCoverProps extends ComponentProps<typeof Box> {
    ratio: number;
    isMobile?: boolean;
    maxHeightOffset?: number;
}

export const PostMediaCover = styled(Box)
    .withConfig({ displayName: 'PostMediaCover' })
    .attrs<MediaCoverProps, MediaCoverProps>((props) => ({
        ...props,
        display: 'block',
        overflow: 'hidden',
    }))`
    position: relative;
    isolation: isolate;

    ${({ isMobile, ratio, maxHeightOffset = 0 }) => {
        if (!ratio) {
            return;
        }

        const clampedRatio = clamp(ratio, MIN_POST_RATIO, 1);
        let paddingBottom = `${(100 / clampedRatio).toFixed(2)}%`;

        if (isMobile) {
            paddingBottom =
                clampedRatio === MIN_POST_RATIO
                    ? `calc(100svh - ${maxHeightOffset}px)`
                    : paddingBottom;
        }

        return css`
            &::after {
                content: '';
                display: block;
                width: 100%;
                height: 0;
                padding-bottom: ${paddingBottom};
            }
        `;
    }}

    ${up('sm')} {
        max-height: 100vh;
    }

    &&& > * {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        height: 100%;
    }
`;

PostMediaCover.defaultProps = {
    backgroundColor: 'var(--placeholder)',
};

const ICON_WHITE = 'var(--always-white)';

export const LikeThumbFilledIcon = () => (
    <IconThumbUpFilled fontSize={24} color={ICON_WHITE} dataTestId={DataTestId.FILLED} />
);

export const LikeThumbIcon = () => (
    <IconThumbUp fontSize={24} color={ICON_WHITE} dataTestId={DataTestId.EMPTY} />
);

export const DislikeThumbFilledIcon = () => (
    <IconThumbDownFilled fontSize={24} color={ICON_WHITE} dataTestId={DataTestId.FILLED} />
);

export const DislikeThumbIcon = () => (
    <IconThumbDown fontSize={24} color={ICON_WHITE} dataTestId={DataTestId.EMPTY} />
);

export const DislikeFilledIcon = () => (
    <IconDislikeFilled
        fontSize={24}
        strokeWidth="2px"
        fill={ICON_WHITE}
        dataTestId={DataTestId.FILLED}
    />
);

export const DislikeIcon = () => (
    <IconDislike fontSize={24} strokeWidth="2px" fill={ICON_WHITE} dataTestId={DataTestId.EMPTY} />
);

export const BookmarkFilledIcon = () => (
    <IconBookmarkFilled fontSize={24} color={ICON_WHITE} dataTestId={DataTestId.FILLED} />
);

export const BookmarkIcon = () => (
    <IconBookmark fontSize={24} color={ICON_WHITE} dataTestId={DataTestId.EMPTY} />
);

export const FavoriteHeartIcon = ({ isActive }: { isActive: boolean }) => (
    <IconLike
        dataTestId={isActive ? DataTestId.FILLED : DataTestId.EMPTY}
        fontSize={24}
        strokeWidth="2px"
        stroke={ICON_WHITE}
        fill={isActive ? ICON_WHITE : 'transparent'}
    />
);
